import {
  useState,
  MouseEventHandler,
  forwardRef,
  useRef,
  useImperativeHandle,
} from 'react'
import { ErrorBoundary } from '@sentry/react'

import { ProductCard } from '@lounge-fe/ui-kit'
import { ImageProps as BaseImageProps } from '@lounge-fe/ui-kit/src/components/Image/types'

import { AddToWishlist } from '../AddToWishlist'
import { QuickAdd } from './QuickAdd'
import { Price } from './Price'
import { StandardProductCardProps, StandardProductCardRef } from './types'
import { useProductBadges } from './hooks/useProductBadges'
import { canonicalColors } from '../../config/canonicalColors'
import { useTranslation } from 'react-i18next'
import { useColors } from '@/hooks/useColors'
import { EmblaCarouselType } from 'embla-carousel'
import { PriceRange } from './PriceRange'

const getSizedImage = (
  image: { src: string; alt: string } | undefined,
  aspectRatio = '2/3',
): BaseImageProps | undefined => {
  if (!image) {
    return undefined
  }

  return {
    ...image,
    src: undefined,
    data: {
      src: image.src,
      ...(aspectRatio === '2/3'
        ? { width: 345, height: 518 }
        : { width: 518, height: 518 }),
      crop: 'center',
    },
  }
}

const BaseStandardProductCard = forwardRef<
  StandardProductCardRef,
  StandardProductCardProps
>(
  (
    {
      product,
      onClick: providedOnClick,
      onQuickAdd,
      useImageCarousel = false,
      useSwatches = false,
      useQuickAdd = false,
      showWishlist = true,
      aspectRatio = '2/3',
      showExclusiveQuickAdd,
      onSuccess,
    },
    ref,
  ) => {
    const { t } = useTranslation()
    const [selectedSwatchId, setSelectedSwatchId] = useState<number>(product.id)
    const { colors } = useColors()

    const selectedSwatch =
      product.swatches.find((swatch) => swatch.id === selectedSwatchId) ??
      product

    const imageCarouselRef = useRef<EmblaCarouselType>()

    useImperativeHandle(ref, () => ({
      imageCarousel: imageCarouselRef.current,
    }))

    const {
      href,
      id,
      handle = '',
      images = [],
      title,
      price,
      price_compare_at,
      color,
      badges = [],
      options = [],
      objectID,
      queryID,
      linkedBottoms = [],
      variants = [],
      tags = [],
      availableForSale,
      isLoadingAdditionalData,
    } = selectedSwatch

    const { variants_min_price, variants_max_price } = selectedSwatch
      ? selectedSwatch
      : product

    const { primaryBadge, secondaryBadge } = useProductBadges({
      badges,
      price,
      price_compare_at,
      tags,
    })

    const onClick: MouseEventHandler<HTMLDivElement> = async (evt) => {
      if (objectID || queryID) {
        window.sessionStorage.setItem(
          `aa_${handle}`,
          JSON.stringify([objectID, queryID]),
        )
      }
      await providedOnClick?.(evt)
    }

    const onSwatchSelect = (id: number | null) => {
      if (id) {
        setSelectedSwatchId(id)
      } else {
        setSelectedSwatchId(product.id)
      }
    }

    const primaryImage = getSizedImage(images[0], aspectRatio)
    const secondaryImage = getSizedImage(images[1], aspectRatio)
    const swatches = [product, ...product.swatches]
    const comingSoon = tags.includes('coming-soon')

    const hasVariablePrice =
      variants_min_price &&
      variants_max_price &&
      variants_min_price !== variants_max_price

    return (
      <ProductCard
        href={href}
        data-testid="product-card"
        onClick={onClick}
        aspectRatio={aspectRatio}
      >
        <ProductCard.ImageWrapper>
          {showWishlist ? (
            <AddToWishlist className="product-card__favourite" productId={id} />
          ) : null}
          {useImageCarousel ? (
            <ProductCard.ImageCarousel
              images={
                images
                  .map((image) => getSizedImage(image, aspectRatio))
                  .filter(Boolean) as BaseImageProps[]
              }
              ref={imageCarouselRef}
            />
          ) : (
            <ProductCard.Image
              primarySrc={primaryImage?.data}
              primaryImageLoading={primaryImage?.loading}
              alt={primaryImage?.alt ?? ''}
              secondarySrc={secondaryImage?.data}
            />
          )}
          {comingSoon ? (
            <ProductCard.Notice color="light">
              {t('product.coming_soon')}
            </ProductCard.Notice>
          ) : (
            <>
              {availableForSale ? (
                /**
                 * TODO: remove linkedBottoms.length < 2 check when we have time to implement a solution
                 * since we've changed the linked_bottoms logic to be AND instead of OR
                 * and we don't have time to impment these changes into the QuickAdd component
                 * for now we'll just show the QuickAdd component if there are less than 2 linkedBottoms
                 */
                <>
                  {useQuickAdd && linkedBottoms.length < 2 && (
                    <QuickAdd
                      exclusive={showExclusiveQuickAdd}
                      key={selectedSwatch.id}
                      handle={handle}
                      options={options}
                      inStock={Boolean(availableForSale && variants.length)}
                      image={images[0]}
                      title={title}
                      price={price}
                      compareAtPrice={price_compare_at}
                      color={color}
                      variants={variants}
                      linkedBottoms={linkedBottoms}
                      onQuickAdd={onQuickAdd}
                      isLoading={isLoadingAdditionalData}
                      onSuccess={onSuccess}
                    />
                  )}
                </>
              ) : (
                <ProductCard.Notice color="dark">
                  {t('product.out_of_stock')}
                </ProductCard.Notice>
              )}
            </>
          )}
        </ProductCard.ImageWrapper>
        <ProductCard.Details
          swatches={useSwatches ? swatches : []}
          onSwatchSelect={onSwatchSelect}
          selectedSwatchId={selectedSwatch.id}
          availableColors={colors}
          canonicalColors={canonicalColors}
          title={title}
          price={
            hasVariablePrice ? (
              <PriceRange
                minPrice={variants_min_price}
                maxPrice={variants_max_price}
              />
            ) : (
              <Price price={price} />
            )
          }
          color={color}
          compareAtPrice={
            price_compare_at && !hasVariablePrice ? (
              <Price price={price_compare_at} />
            ) : null
          }
          primaryBadge={primaryBadge}
          secondaryBadge={secondaryBadge}
          isLoadingAdditionalData={product.isLoadingAdditionalData}
          useSwatches={useSwatches}
        />
      </ProductCard>
    )
  },
)

export const StandardProductCard = forwardRef<
  StandardProductCardRef,
  StandardProductCardProps
>((props, ref) => {
  const { aspectRatio, ...restProps } = props
  return (
    <ErrorBoundary>
      <BaseStandardProductCard
        {...restProps}
        ref={ref}
        aspectRatio={aspectRatio}
      />
    </ErrorBoundary>
  )
})
